import { useContext, useEffect, useMemo, useRef, useState, Fragment } from "react";
import { CAMPAIGN_SUBTYPES_ENUM, CAMPAIGN_ENUM_TYPES } from "shared/constants";
import { Helmet } from "react-helmet";
import { usePostHog } from "posthog-js/react";
import cn from "classnames";
import { useHistory } from "react-router";
import { generatePath } from "react-router-dom";
import { SnackBarContext } from "components/snack-bar";
import { debounce } from "lodash";
import { EmitAnalyticsEvent } from "module/analytics/application/EmitAnalyticsEvent";
import { useJsApiLoader } from "@react-google-maps/api";
import { CustomTooltipContent } from "components/tooltip-content";
import { EDDMHeader } from "components/header/EDDMHeader";
import BounceLoader from "components/loaders/bounce-loader";
import { IfElse } from "components/logic";
import { ProgressBarContext } from "components/progress-bar";
import { EDDMSegmentsOriginator, EDDMSegmentsCaretaker } from "module/eddm";
import { checkIsNeedUpdateLastActiveStepState } from "utils";
import { AudienceMapEDDMV3 } from "components/audience-map/components/AudienceMapEDDM/AudienceMapEDDMV3";
import CONFIG from "config/config";
import { EDDM_ROUTING } from "config/routing";
import {
  getSelectedRoutesFromDistributionLocations,
  radiusToZoom,
} from "components/audience-map/components/AudienceMapEDDM/utils";
import { EDDMEmptyStateV2, EDDMToolbar } from "components/audience-map/components";
import {
  calculateCircleRadius,
  getCarrierRoutesWithSuggestions,
  recalculateSuggestedRoutes,
  recalculateAndUpdateSuggestedRoutesOnDataLayer,
} from "components/audience-map/utils";
import {
  walkCarrierRoutePages,
  routeWalkErrorMessage,
  routeWalkErrorDetail,
  routeWalkNotice,
} from "components/audience-map/routeWalk";
import useExitPrompt from "Hooks/useExitPrompt";
import { EddmOadsSidebar } from "campaign-builder/EddmOadsSidebar";
import { apiClient } from "../../../module/api";

import {
  CAMPAIGN_INTERNAL_STATUSES,
  EDDM_NAVIGATION,
  COUPON_CODE_ERROR_MESSAGE,
  CAMPAIGN_CHANNELS_MIN_RECIPIENTS,
} from "../../constants";
import {
  createQuote,
  updateCampaignExtraData,
  updateSelfServeCampaignData,
  updateCampaign as updateCampaignDB,
} from "../../../graphQL";
import BlockingOverlay from "../../../components/BlockingOverlay";
import { useStore } from "../../../store";
import "./styles.css";

const libraries = ["geometry", "places", "drawing"];
// Radius the map opens with for every new location (miles). 5 mi shows enough routes to start from.
const DEFAULT_RADIUS_MILES = 5;
const radiusOptions = [1, 2, 5, 10].map((v) => ({
  value: v,
  label: `${v} miles`,
}));

export const SegmentsV3 = () => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: CONFIG.GOOGLE_API_KEY,
    libraries: libraries,
    language: "en",
    version: "3.64",
  });

  const posthog = usePostHog();
  const postHogTracker = new EmitAnalyticsEvent(posthog);
  const {
    costsCalculationData,
    country,
    user,
    campaign: {
      channel,
      campaignName,
      internalStatus,
      isSubmitted,
      id: campaignId,
      lastActiveStep,
      stripeCouponCode,
      subtype,
    },
    errors: { campaignNameDuplicates },
    user: { accessDenied = true, id: userId },
    client: { id: clientId, name: clientName },
    map: { center, loading, loadingPercentage, visiblePostCodes = [], shouldShowDefaultPin },
    selectedTargets,
    distributionLocations,
    setDistributionLocations,
    setFocusedLocation,
    updateCampaign,
    updateMap,
    updateErrors,
    setSuggestedRoutes,
    suggestionsEnabled,
    suggestedRoutes,
    dismissedSuggestedRoutes,
    setDismissedSuggestedRoutes,
    setSuggestionsEnabled,
    client,
  } = useStore();

  const history = useHistory();

  // useState
  const [, setShowExitPrompt] = useExitPrompt(false);
  const [radius, setRadius] = useState(DEFAULT_RADIUS_MILES);
  const [couponErrorMessage, setCouponErrorMessage] = useState("");
  // Route (single click) is the default tool; the Area brush is opt-in from
  // the toolbar (Slava, 2026-09-17).
  const [drawingMode, setDrawingMode] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [circleSelectorRadius, setCircleSelectorRadius] = useState(calculateCircleRadius(radius));
  const [isCalculatingMultipleSelection, setIsCalculatingMultipleSelection] = useState(false);
  const [googleMap, setGoogleMap] = useState(null);
  const [shouldHideStatistics, setShouldHideStatistics] = useState(false);
  const [selectedRoutes, setSelectedRoutes] = useState(
    getSelectedRoutesFromDistributionLocations(distributionLocations)
  );
  const [shouldAutoSave, setShouldAutoSave] = useState(false);
  const [error, setError] = useState(true);
  const [isBlocked, setIsBlocked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isEdited, setIsEdited] = useState(false);
  const [validatingCampaignName, setValidatingCampaignName] = useState(false);
  const [isUndoOrRedo, setIsUndoOrRedo] = useState(false);
  const [isSingleRouteSelection, setIsSingleRouteSelection] = useState(false);
  const [routes, setRoutes] = useState({});

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fromHome = useMemo(() => lastActiveStep === "home", [lastActiveStep]);

  const { totalCost, tax } = costsCalculationData?.detailedCost || {};

  const runSnackBar = useContext(SnackBarContext);
  const runProgressBar = useContext(ProgressBarContext);

  // useRef
  const clickSaveButtonRef = useRef(false);
  // Last { center, radius } handed to fetchRoutes, so the map's Retry control
  // can re-run exactly the lookup that failed. Written inside fetchRoutes
  // itself, which is the only place `loadingError` is ever set — so every entry
  // point (search, radius change, undo/redo from the toolbar, retry) records
  // what to retry, not just the ones that go through fetchRoutesAndUpdateMap.
  const lastRouteRequestRef = useRef(null);
  // Aborts the walk AND the in-flight GraphQL request behind it, so Cancel is
  // immediate rather than "stop after the current page finally comes back".
  const routeAbortRef = useRef(null);
  // Monotonic id of the in-flight route lookup. A fast second click used to let
  // the slower first walk land last and overwrite the newer result.
  const routeRequestIdRef = useRef(0);
  const originator = useRef(new EDDMSegmentsOriginator(distributionLocations || [])).current;
  const caretaker = useRef(new EDDMSegmentsCaretaker(originator)).current;

  // useMemo
  const isNextActive = useMemo(() => {
    return (
      costsCalculationData.flyersAmount &&
      costsCalculationData.flyersAmount >= CAMPAIGN_CHANNELS_MIN_RECIPIENTS[CAMPAIGN_SUBTYPES_ENUM.EDDM]
    );
  }, [costsCalculationData.flyersAmount, isSubmitted]);
  // useEffect

  useEffect(() => {
    if (isLoaded && internalStatus === CAMPAIGN_INTERNAL_STATUSES.DRAFT && stripeCouponCode) {
      getStripeCouponData({ stripeCouponCode });
    }
  }, [isLoaded, stripeCouponCode, internalStatus]);

  useEffect(() => {
    if (campaignNameDuplicates) {
      setError("Duplicate campaign name. Please choose another.");
      return;
    }
  }, [campaignNameDuplicates]);

  useEffect(() => {
    caretaker.clearHistory(campaignId);
    originator.setState(distributionLocations || []);
    caretaker.save(campaignId);
    if (distributionLocations && distributionLocations.length) {
      const focusedLocation = distributionLocations.find((location) => location.isFocused);
      if (focusedLocation) {
        setRadius(focusedLocation.radius);
        updateMap({
          center: { lat: focusedLocation.lat, lng: focusedLocation.lng, zoom: radiusToZoom[focusedLocation.radius] },
        });
      }
      return () => {
        caretaker.clearHistory(campaignId);
      };
    }
  }, []);

  const saveDataToDB = debounce(() => {
    handleAutoSave();
    setShouldAutoSave(false);
  }, 1000);

  useEffect(() => {
    if (distributionLocations && shouldAutoSave) {
      if (isSingleRouteSelection) {
        updateEDDMSegmentsCaretaker();
        setIsSingleRouteSelection(false);
      }
      saveDataToDB();
    }
    return () => {
      saveDataToDB.cancel();
    };
  }, [distributionLocations]);

  useEffect(() => {
    if (selectedTargets && shouldAutoSave) {
      saveDataToDB();
      setDismissedSuggestedRoutes([]);
      recalculateAndUpdateSuggestedRoutesOnDataLayer({
        routes,
        selectedTargets,
        selectedRoutes,
        setSuggestedRoutes,
        googleMap,
        suggestionsEnabled,
        dismissedSuggestedRoutes: [],
      });
    }
    return () => {
      saveDataToDB.cancel();
    };
  }, [selectedTargets]);

  useEffect(() => {
    if (selectedTargets && shouldAutoSave) {
      saveDataToDB();
      recalculateAndUpdateSuggestedRoutesOnDataLayer({
        routes,
        selectedTargets,
        selectedRoutes,
        setSuggestedRoutes,
        googleMap,
        suggestionsEnabled,
        dismissedSuggestedRoutes,
      });
    }
    return () => {
      saveDataToDB.cancel();
    };
  }, [suggestionsEnabled]);

  const updateEDDMSegmentsCaretaker = () => {
    if (
      !isUndoOrRedo &&
      distributionLocations &&
      distributionLocations?.length &&
      JSON.stringify(distributionLocations) !== JSON.stringify(originator.getState())
    ) {
      originator.setState(distributionLocations);
      caretaker.save(campaignId);
    }

    if (!distributionLocations || !distributionLocations.length) {
      caretaker.clearHistory(campaignId);
      originator.setState([]);
    }
  };

  // useCallback
  const saveCampaign = async (isAutoSave) => {
    setIsSaving(true);
    if (!isSubmitted && distributionLocations) {
      const payloadCampaignExtraData = {
        campaignId,
        totalCosts: totalCost,
        flyersCount: costsCalculationData?.flyersAmount || 0,
        taxes: tax,
      };

      const isNeedUpdateLastActiveStep = checkIsNeedUpdateLastActiveStepState({
        stateLastActiveStep: lastActiveStep,
        newLastActiveStep: "segments",
        channel: subtype,
      });

      if (isNeedUpdateLastActiveStep && !isAutoSave) {
        payloadCampaignExtraData.lastActiveStep = "segments";
      }

      await updateCampaignExtraData(payloadCampaignExtraData);

      updateEDDMSegmentsCaretaker();

      await updateSelfServeCampaignData({
        campaignId: campaignId,
        distributionLocations: distributionLocations,
      });
      if (isNeedUpdateLastActiveStep && !isAutoSave) {
        updateCampaign({ lastActiveStep: "segments" });
      }
      await setQuoteNumber(campaignId);
      if (!isAutoSave) {
        nextBtnClicked(campaignId);
      }
    }
    setIsSaving(false);
  };

  const handleAutoSave = async () => {
    setIsSaving(true);
    if (!isSingleRouteSelection) {
      updateEDDMSegmentsCaretaker();
    }
    if (!distributionLocations?.length && lastActiveStep !== "home") {
      await updateCampaignExtraData({
        campaignId,
        lastActiveStep: "home",
      });
      updateCampaign({ lastActiveStep: "home" });
    }
    await updateSelfServeCampaignData({
      campaignId: campaignId,
      distributionLocations: distributionLocations,
      selectedDemographicAttributes: selectedTargets,
      suggestionsEnabled: suggestionsEnabled,
    });
    setIsSaving(false);
    setIsCalculatingMultipleSelection(false);
  };

  /**
   * Paginated carrier-route lookup for one location.
   *
   * The walk is bounded (deadline + page cap) and always ends with
   * `loading: false`, so a slow or dead pathfinder can no longer leave the
   * "Loading distribution areas" spinner up with no way out. On failure the
   * map store carries a `loadingError` the map renders with a Retry control.
   */
  const fetchRoutes = async ({ center, radius }) => {
    routeRequestIdRef.current += 1;
    const requestId = routeRequestIdRef.current;
    const isStale = () => routeRequestIdRef.current !== requestId;
    // Recorded here rather than in fetchRoutesAndUpdateMap: the toolbar's
    // undo/redo path calls fetchRoutes directly, and previously left Retry
    // pointing at a stale location/radius (or at nothing at all).
    lastRouteRequestRef.current = { center, radius };

    // A superseded walk's controller is dropped, not aborted: the newer walk
    // owns the spinner, and aborting the old one would paint a cancel state
    // over a lookup that is still legitimately running.
    const abortController = typeof AbortController === "function" ? new AbortController() : null;
    routeAbortRef.current = abortController;

    try {
      const result = await walkCarrierRoutePages({
        signal: abortController ? abortController.signal : undefined,
        fetchPage: ({ after, accumulatedSuggestions, signal }) =>
          getCarrierRoutesWithSuggestions({
            after,
            centerSphere: center,
            radius,
            targets: selectedTargets,
            // Threaded page-to-page: pathfinder folds this into its global
            // top-5. Passing a constant [] would yield the last page's top-5.
            accumulatedSuggestions,
            signal,
          }),
        onProgress: ({ percentage }) => {
          if (!isStale()) updateMap({ loadingPercentage: percentage });
        },
      });

      if (isStale()) {
        return result.routes || {};
      }

      const newRoutes = result.routes || {};

      if (!result.ok) {
        // Keep whatever pages did arrive on screen, but say plainly that the
        // rest failed rather than presenting a partial map as complete.
        setRoutes(newRoutes);
        updateMap({
          loadingError: routeWalkErrorMessage(result),
          loadingErrorDetail: routeWalkErrorDetail(result),
          loadingNotice: null,
        });
        return newRoutes;
      }

      const newSuggestedRoutes = recalculateSuggestedRoutes(
        newRoutes,
        selectedTargets,
        selectedRoutes,
        dismissedSuggestedRoutes
      );
      setSuggestedRoutes(newSuggestedRoutes);
      setRoutes(newRoutes);
      updateMap({ loadingError: null, loadingErrorDetail: null, loadingNotice: routeWalkNotice(result) });
      return newRoutes;
    } finally {
      if (routeAbortRef.current === abortController) {
        routeAbortRef.current = null;
      }
      // Belt and braces: no code path above may leave the spinner running.
      // A superseded walk leaves the flags to the newer one.
      if (!isStale()) {
        updateMap({ loading: false, loadingPercentage: null });
      }
    }
  };

  const setQuoteNumber = async (campaignId) => {
    const quote = await createQuote(campaignId);
    updateCampaign({ quote });
  };

  const nextBtnClicked = (id) => {
    // Always navigate to Flyer page (Design + Tracking combined)
    return history.push(generatePath(EDDM_ROUTING.EDDM_FLYER, { campaignId: id, clientId }));
  };

  const headerActions = {
    HOME: {
      action: () => {
        console.log("redirect home");
      },
    },
    NEXT: {
      action: async () => {
        if (isSubmitted) {
          runProgressBar(60);
          nextBtnClicked(campaignId);
          runProgressBar(80);
          runProgressBar(-1);
        } else {
          clickSaveButtonRef.current = false;
          await saveCampaign();
        }
      },
    },
  };

  const getStripeCouponData = async ({ stripeCouponCode }) => {
    if (stripeCouponCode) {
      const code = stripeCouponCode.trim();
      try {
        const res = await apiClient.getPromotionCode({
          promotionCode: code,
          countryCode: country?.code,
        });
        if (res.status !== 200) {
          throw new Error(COUPON_CODE_ERROR_MESSAGE["DEFAULT"]);
        }
        const { promotionCodes: responseData } = await res.json();
        if (!responseData) {
          throw new Error(COUPON_CODE_ERROR_MESSAGE["DEFAULT"]);
        }

        const { data } = responseData;
        if (!data || !data[0] || code !== data[0].code) {
          throw new Error(COUPON_CODE_ERROR_MESSAGE["DEFAULT"]);
        }

        const {
          id: promoId,
          coupon: couponObject,
          max_redemptions: max_redemptions_promotion_code,
          times_redeemed: times_redeemed_promotion_code,
          restrictions,
        } = data[0];

        if (!couponObject || !couponObject.id || !couponObject.percent_off) {
          throw new Error(COUPON_CODE_ERROR_MESSAGE["NO_PROMO_EXIST"]);
        }
        if (
          max_redemptions_promotion_code &&
          times_redeemed_promotion_code &&
          max_redemptions_promotion_code <= times_redeemed_promotion_code
        ) {
          throw new Error(COUPON_CODE_ERROR_MESSAGE["EXCEED_USE_PROMO"]);
        }
        if (!max_redemptions_promotion_code && !times_redeemed_promotion_code && !restrictions) {
          throw new Error(COUPON_CODE_ERROR_MESSAGE["DEFAULT"]);
        }

        const { max_redemptions: max_redemptions_coupon, times_redeemed: times_redeemed_coupon } = couponObject;
        if (max_redemptions_coupon && times_redeemed_coupon && max_redemptions_coupon <= times_redeemed_coupon) {
          throw new Error(COUPON_CODE_ERROR_MESSAGE["EXCEED_USE_COUPON"]);
        }
        if (!max_redemptions_coupon && !times_redeemed_coupon && !restrictions) {
          throw new Error(COUPON_CODE_ERROR_MESSAGE["DEFAULT"]);
        }

        if (restrictions) {
          const { first_time_transaction } = restrictions;
          if (first_time_transaction) {
            // TO DO: SAVE THIS ON STRIPE CUSTOMER METADATA OR ON THE OPPIZI USER
            const didReedemed = !!localStorage.getItem(`first_time_transaction_${promoId}`);
            if (didReedemed) {
              throw new Error(COUPON_CODE_ERROR_MESSAGE["ONLY_FIRST"]);
            }
          }
        }

        const percent_off = couponObject.percent_off;

        runSnackBar({
          type: "success",
          vertical: "top",
          msg: `Your ${percent_off}% discount will be applied at checkout.`,
        });
      } catch (err) {
        setCouponErrorMessage(err.message);
      }
    }
    return false;
  };

  const handleAutocompleteChanged = (newMarker) => {
    onPlaceChanged(newMarker, () =>
      postHogTracker.run({
        eventName: "Searched initial address to EDDM",
        userId: user?.id,
        clientName,
        device: navigator.userAgent,
      })
    );
  };

  const isValidLocation = (location) => location.lat && location.lng;
  const isValidMarker = (marker) => marker?.geometry?.location?.lat() && marker?.geometry?.location?.lng();

  const compareMarker = (location, marker) => {
    if (!isValidLocation(location) || !isValidMarker(marker)) {
      return false;
    }

    return location.lat === marker.geometry.location.lat() && location.lng === marker.geometry.location.lng();
  };

  const handleDistributionLocations = (newMarker) => {
    if (!newMarker) {
      return;
    }

    const lat = newMarker.geometry?.location?.lat();
    const lng = newMarker.geometry?.location?.lng();
    const locationExists = !!distributionLocations.find((location) => compareMarker(location, newMarker));

    if (locationExists) {
      setDistributionLocations([
        ...distributionLocations.map((l) => ({
          ...l,
          isFocused: compareMarker(l, newMarker),
        })),
      ]);
      return;
    }

    setDistributionLocations([
      {
        name: newMarker.formattedAddress,
        lat,
        lng,
        radius: DEFAULT_RADIUS_MILES,
        isFocused: true,
        selectedRoutes: [],
      },
      ...distributionLocations.map((l) => ({ ...l, isFocused: false })),
    ]);
    setRadius(DEFAULT_RADIUS_MILES);
  };

  const onPlaceChanged = (newMarker, onInitialPlaceFetch) => {
    if (!newMarker) {
      return;
    }

    const lat = newMarker.geometry?.location?.lat();
    const lng = newMarker.geometry?.location?.lng();

    if (onInitialPlaceFetch) {
      onInitialPlaceFetch();
    } else {
      setSuggestionsEnabled(false);
    }
    setShouldAutoSave(true);
    setIsUndoOrRedo(false);
    handleDistributionLocations(newMarker);
    updateMap({ center: { lat, lng } });
  };

  const fetchRoutesAndUpdateMap = (focusedLocation, radius) => {
    updateMap({
      loading: true,
      loadingError: null,
      loadingErrorDetail: null,
      loadingNotice: null,
      center: { lat: focusedLocation.lat, lng: focusedLocation.lng },
    });
    // The promise was previously started un-awaited and un-caught: an unexpected
    // throw escaped as an unhandled rejection and the spinner stayed up.
    return fetchRoutes({ center: [focusedLocation.lng, focusedLocation.lat], radius: radius * 1609.34 }).catch(
      (error) => {
        console.error("EDDM carrier-route lookup failed:", error);
        updateMap({
          loading: false,
          loadingPercentage: null,
          loadingError: routeWalkErrorMessage({ ok: false }),
          loadingErrorDetail: routeWalkErrorDetail({ ok: false }),
        });
      }
    );
  };

  /**
   * Re-runs the last route lookup — wired to the map's error-state Retry control.
   * Replays the exact { center, radius } fetchRoutes last ran, whichever entry
   * point started it, so Retry can never re-run a different area than the one
   * the toolbar is showing.
   */
  const retryFetchRoutes = () => {
    const lastRequest = lastRouteRequestRef.current;
    if (!lastRequest) {
      updateMap({ loadingError: null, loadingErrorDetail: null });
      return;
    }
    const [lng, lat] = lastRequest.center;
    updateMap({
      loading: true,
      loadingError: null,
      loadingErrorDetail: null,
      loadingNotice: null,
      center: { lat, lng },
    });
    return fetchRoutes(lastRequest).catch((error) => {
      console.error("EDDM carrier-route lookup failed:", error);
      updateMap({
        loading: false,
        loadingPercentage: null,
        loadingError: routeWalkErrorMessage({ ok: false }),
        loadingErrorDetail: routeWalkErrorDetail({ ok: false }),
      });
    });
  };

  /**
   * Abandons the in-flight carrier-route lookup and hands the map back.
   *
   * With a 300s walk budget the worst case is five minutes of spinner; before
   * this the only way out was reloading the page. The walk resolves with
   * `reason: "cancelled"` (never "timeout"), so the terminal screen says the
   * user stopped it and offers Retry.
   */
  const cancelFetchRoutes = () => {
    const abortController = routeAbortRef.current;
    if (!abortController) {
      updateMap({ loading: false, loadingPercentage: null });
      return;
    }
    abortController.abort();
  };

  useEffect(() => {
    if (distributionLocations && distributionLocations.length) {
      setDistributionLocations(
        distributionLocations.map((location) => {
          if (location.isFocused) {
            return { ...location, radius: radius };
          } else {
            return location;
          }
        })
      );
    }
    setCircleSelectorRadius(calculateCircleRadius(radius));
    // setDrawingMode(null);
  }, [radius]);

  const getFilteredPostCodes = (key) => [...visiblePostCodes.filter((postCode) => postCode?.name?.includes(key))];

  return (
    <Fragment>
      {isBlocked ? <BlockingOverlay /> : null}
      <Helmet>
        <title>Segments</title>
      </Helmet>
      <div className="flex h-screen w-full overflow-hidden">
        <EddmOadsSidebar country={country} client={client} collapseOnMount />
        <main className="flex h-screen min-w-0 flex-1 flex-col overflow-hidden" data-copilot-step="EDDM Targeting">
          <EDDMHeader
            tabs={EDDM_NAVIGATION}
            activeTabIndex={1}
            headerActions={headerActions}
            hideBackButton={isSubmitted || !!campaignId}
            hasError={!isNextActive || isEdited}
            isNextActive={isNextActive}
            isDM={channel === CAMPAIGN_ENUM_TYPES.DIRECTMAIL}
            selectedRoutes={selectedRoutes}
            setShouldHideStatistics={setShouldHideStatistics}
          />
          <div
            // `relative` anchors the EDDMEmptyStateV2 scrim to the map column instead of the viewport.
            className="relative z-[2] flex min-h-0 w-full flex-1 flex-col"
          >
            {isLoaded && !distributionLocations?.length && (
              <EDDMEmptyStateV2
                countryCode={country?.code}
                disablePlaceAutocomplete={isSubmitted || loading}
                handleAutocompleteChanged={handleAutocompleteChanged}
                getFilteredPostCodes={getFilteredPostCodes}
                setDrawingMode={setDrawingMode}
              />
            )}

            <div className={cn("mob-map-container h-full w-full min-w-[500px]")}>
              <IfElse condition={isLoaded}>
                <div className="relative flex h-full w-full flex-row flex-nowrap justify-center p-0">
                  <AudienceMapEDDMV3
                    isSubmitted={isSubmitted}
                    radius={radius}
                    setRadius={setRadius}
                    selectedRoutes={selectedRoutes}
                    setSelectedRoutes={setSelectedRoutes}
                    shouldAutoSave={shouldAutoSave}
                    setShouldAutoSave={setShouldAutoSave}
                    drawingMode={drawingMode}
                    setDrawingMode={setDrawingMode}
                    circleSelectorRadius={circleSelectorRadius}
                    isUndoOrRedo={isUndoOrRedo}
                    setIsUndoOrRedo={setIsUndoOrRedo}
                    routes={routes}
                    setRoutes={setRoutes}
                    fetchRoutes={fetchRoutes}
                    fetchRoutesAndUpdateMap={fetchRoutesAndUpdateMap}
                    retryFetchRoutes={retryFetchRoutes}
                    cancelFetchRoutes={cancelFetchRoutes}
                    setCircleSelectorRadius={setCircleSelectorRadius}
                    isCalculatingMultipleSelection={isCalculatingMultipleSelection}
                    setIsCalculatingMultipleSelection={setIsCalculatingMultipleSelection}
                    setIsSingleRouteSelection={setIsSingleRouteSelection}
                    googleMap={googleMap}
                    setGoogleMap={setGoogleMap}
                    onPlaceChanged={onPlaceChanged}
                    getFilteredPostCodes={getFilteredPostCodes}
                    shouldHideStatistics={shouldHideStatistics}
                    setShouldHideStatistics={setShouldHideStatistics}
                  />
                  {!!distributionLocations?.length && !isMobile && !isSubmitted && (
                    <EDDMToolbar
                      radius={radius}
                      setRadius={setRadius}
                      isSaving={isSaving}
                      radiusOptions={radiusOptions}
                      drawingMode={drawingMode}
                      setDrawingMode={setDrawingMode}
                      setShouldAutoSave={setShouldAutoSave}
                      circleSelectorRadius={circleSelectorRadius}
                      setCircleSelectorRadius={setCircleSelectorRadius}
                      setIsUndoOrRedo={setIsUndoOrRedo}
                      setSelectedRoutes={setSelectedRoutes}
                      fetchRoutes={fetchRoutes}
                      originator={originator}
                      caretaker={caretaker}
                      fetchRoutesAndUpdateMap={fetchRoutesAndUpdateMap}
                      googleMap={googleMap}
                      setShouldHideStatistics={setShouldHideStatistics}
                    />
                  )}
                </div>
                <div className="flex h-full items-center justify-center">
                  <BounceLoader />
                </div>
              </IfElse>
            </div>
          </div>
        </main>
      </div>
    </Fragment>
  );
};
